<template>
  <div class="table-order-scanner">
    <!-- Trigger Button -->
    <div v-if="!scanning" class="text-center">
      <button @click="startScan"
        class="bg-secondary-0 text-primary-0 font-bold py-4 px-8 rounded-xl flex items-center justify-center mx-auto">
        <font-awesome-icon :icon="['fas', 'qrcode']" class="text-2xl mr-3" />
        <span>{{ $t('scan_table_qr') }}</span>
      </button>

      <!-- Error message -->
      <div v-if="error" class="mt-4 bg-danger-0 p-3 rounded-lg text-white text-center">
        {{ error }}
      </div>
    </div>

    <!-- Fullscreen Scanner Overlay -->
    <teleport to="body">
      <div v-if="scanning" class="scanner-container">
        <!-- Top bar with back button -->
        <div class="scanner-top-bar">
          <button @click="stopScan" class="flex items-center text-white hover:opacity-75">
            <font-awesome-icon :icon="['fal', 'angle-left']" class="text-2xl mr-3" />
            <span>{{ $t('back') }}</span>
          </button>
        </div>

        <!-- Scanner frame border -->
        <div class="scanner-frame"></div>

        <!-- Semi-transparent overlay with cutout -->
        <div class="scanner-overlay"></div>

        <!-- Laser animation -->
        <div class="scanner-laser"></div>

        <!-- Status messages -->
        <div class="scanner-status">
          <p v-if="loading" class="text-white text-lg">{{ $t('scanner_loading') }}</p>
          <p v-else class="text-white text-lg opacity-75">{{ $t('point_camera_at_qr') }}</p>
        </div>

        <!-- Bottom bar with cancel button -->
        <div class="scanner-bottom-bar">
          <button @click="stopScan" class="bg-danger-0 text-white font-bold py-3 px-8 rounded-xl">
            {{ $t('cancel') }}
          </button>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'
import { BarcodeScanner } from '@capacitor-community/barcode-scanner'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { useI18n } from "vue-i18n"
import useDeliveryStore from "@/stores/deliveryStore"
import useContentStore from "@/stores/contentStore"
import useCartStore from "@/stores/cartStore"
import { Capacitor } from "@capacitor/core"

const { t } = useI18n()
const deliveryStore = useDeliveryStore()
const contentStore = useContentStore()
const cartStore = useCartStore()

const emit = defineEmits(['scanned', 'error'])

const scanning = ref(false)
const loading = ref(false)
const error = ref(null)

const parseQRCode = (content) => {
  try {
    // Format: hajcer://table/{place}/{blueGastroId}/{tableNumber}
    // Priklad: hajcer://table/BO/25/5
    if (content.startsWith('hajcer://table/')) {
      const parts = content.replace('hajcer://table/', '').split('/')
      if (parts.length >= 3) {
        return {
          place: parts[0],           // BO alebo PE
          pluTableId: parseInt(parts[1]),  // BlueGastro ID stola
          tableNumber: parts[2]      // Cislo stola pre zobrazenie
        }
      }
    }

    // Alternativny format: JSON
    const data = JSON.parse(content)
    if (data.place && data.pluTableId) {
      return {
        place: data.place,
        pluTableId: data.pluTableId,
        tableNumber: data.tableNumber || data.pluTableId.toString()
      }
    }

    return null
  } catch {
    return null
  }
}

const addScannerClass = () => {
  document.getElementsByTagName('html')[0]?.classList.add('scanner-active')
}

const removeScannerClass = () => {
  document.getElementsByTagName('html')[0]?.classList.remove('scanner-active')
}

const devTableQrUrl = import.meta.env.VITE_DEV_TABLE_QR_URL

const startScan = async () => {
  // Web: použiť mock QR z .env pri lokálnom vývoji
  if (Capacitor.getPlatform() === 'web') {
    if (devTableQrUrl) {
      error.value = null
      await handleScanResult(devTableQrUrl)
      return
    }
    error.value = t('scanner_not_available_web')
    return
  }

  error.value = null
  loading.value = true
  scanning.value = true

  try {
    // Add transparent class to HTML
    addScannerClass()

    // Check camera permission
    const status = await BarcodeScanner.checkPermission({ force: true })

    if (status.denied) {
      // User denied permission permanently
      const shouldOpenSettings = confirm(t('scanner_permission_denied'))
      if (shouldOpenSettings) {
        await BarcodeScanner.openAppSettings()
      }
      await stopScan()
      return
    }

    if (!status.granted) {
      // Permission not granted for other reason
      error.value = t('scanner_permission_denied')
      await stopScan()
      return
    }

    // Make background transparent
    await BarcodeScanner.hideBackground()
    loading.value = false

    // Start scanning
    const result = await BarcodeScanner.startScan()

    // Process result
    if (result.hasContent) {
      await handleScanResult(result.content)
    } else {
      error.value = t('scan_error')
      emit('error', error.value)
    }

    await stopScan()

  } catch (e) {
    console.error('Scan error:', e)
    error.value = t('scan_error')
    emit('error', error.value)
    await stopScan()
  }
}

const handleScanResult = async (content) => {
  const parsed = parseQRCode(content)

  if (parsed) {
    // Validate place
    if (parsed.place !== 'BO' && parsed.place !== 'PE') {
      error.value = t('invalid_qr_place')
      emit('error', error.value)
      return
    }

    // Set table order in store
    deliveryStore.setTableOrder(parsed.place, parsed.tableNumber, parsed.pluTableId)

    // Clear cart and set content for location
    cartStore.itemsInCart = []
    await contentStore.mapAppContent()

    emit('scanned', parsed)
  } else {
    error.value = t('invalid_qr')
    emit('error', error.value)
  }
}

const stopScan = async () => {
  try {
    await BarcodeScanner.showBackground()
    await BarcodeScanner.stopScan()
  } catch (e) {
    console.error('Stop scan error:', e)
  }

  removeScannerClass()
  scanning.value = false
  loading.value = false
}

// Cleanup on unmount
onUnmounted(async () => {
  if (scanning.value) {
    await stopScan()
  }
})
</script>

<style scoped lang="scss">
.scanner-container {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: transparent;
  visibility: visible !important;
}

.scanner-top-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10010;
  padding: calc(env(safe-area-inset-top) + 16px) 20px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: linear-gradient(to bottom, rgba(30, 30, 30, 0.8) 0%, transparent 100%);
}

.scanner-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  height: 280px;
  border: 6px solid #fbb665; // secondary-0
  border-radius: 24px;
  z-index: 10005;
  box-sizing: border-box;
}

.scanner-overlay {
  position: absolute;
  inset: 0;
  z-index: 10003;
  // Použijeme 4 prekrývajúce elementy namiesto clip-path
  background:
    linear-gradient(to bottom, rgba(30, 30, 30, 0.7) 0%, rgba(30, 30, 30, 0.7) calc(50% - 140px), transparent calc(50% - 140px), transparent calc(50% + 140px), rgba(30, 30, 30, 0.7) calc(50% + 140px), rgba(30, 30, 30, 0.7) 100%),
    linear-gradient(to right, rgba(30, 30, 30, 0.7) 0%, rgba(30, 30, 30, 0.7) calc(50% - 140px), transparent calc(50% - 140px), transparent calc(50% + 140px), rgba(30, 30, 30, 0.7) calc(50% + 140px), rgba(30, 30, 30, 0.7) 100%);
  background-blend-mode: darken;
}

.scanner-laser {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60%;
  height: 3px;
  background: #fbb665; // secondary-0
  box-shadow: 0 0 8px #fbb665, 0 0 16px rgba(251, 182, 101, 0.5);
  z-index: 10004;
  animation: laser-scan 2s ease-in-out infinite;
  border-radius: 2px;
}

@keyframes laser-scan {

  0%,
  100% {
    transform: translate(-50%, -50%) translateY(-80px);
  }

  50% {
    transform: translate(-50%, -50%) translateY(80px);
  }
}

.scanner-status {
  position: absolute;
  top: 20%;
  left: 0;
  right: 0;
  z-index: 10010;
  text-align: center;
  padding: 0 20px;
}

.scanner-bottom-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10010;
  padding: 24px;
  padding-bottom: calc(env(safe-area-inset-bottom) + 24px);
  display: flex;
  justify-content: center;
  background: linear-gradient(to top, rgba(30, 30, 30, 0.8) 0%, transparent 100%);
}
</style>
